import './styles.scoped.css'
import {useState} from 'react'
import {Typography, IconButton, Collapse, Chip, Skeleton} from '@mui/material'
import {ExpandMore, ExpandLess, WarningAmber} from '@mui/icons-material'
import {useMobile} from '../../../../hooks'
import {SkeletonContainer, TextSkeleton, ChipSkeleton} from '../../../../components'

// The value side of one row while the visit's times and locations load.
const ValueSkeleton = ({isLocation, width}) =>
    isLocation ? (
        <div className="location-map">
            <Skeleton variant="rounded" width={100} height={70} sx={{borderRadius: '8px'}} />
            <TextSkeleton width={width} />
        </div>
    ) : (
        <TextSkeleton width={width} />
    )

const RowsSkeleton = ({rows, mobile}) => (
    <SkeletonContainer>
        {!mobile && (
            <div className="table">
                <div className="row row-header">
                    <div className="cell-label" />
                    <div className="row-data">
                        <div className="cell">
                            <Typography variant="caption" color="text.secondary">
                                SCHEDULED
                            </Typography>
                        </div>
                        <div className="cell">
                            <Typography variant="caption" color="text.secondary">
                                ACTUAL (CAREGIVER)
                            </Typography>
                        </div>
                        <div className="cell-status" />
                    </div>
                </div>
                {rows?.map((row, index) => (
                    <div className="row" key={index}>
                        <div className="cell-label">
                            <Typography variant="h9" color="text.primary">
                                {row.label}
                            </Typography>
                        </div>
                        <div className="row-data">
                            <div className="cell">
                                <ValueSkeleton isLocation={row.isLocation} width={row.isLocation ? '80%' : 64} />
                            </div>
                            <div className="cell">
                                <ValueSkeleton isLocation={row.isLocation} width={row.isLocation ? '70%' : 72} />
                            </div>
                            <div className="cell-status">
                                <ChipSkeleton width={70} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )}
        {mobile &&
            rows?.map((row, index) => (
                <div className="row" key={index} style={{flexDirection: 'column', alignItems: 'start'}}>
                    <div className="cell-label">
                        <Typography variant="h9" color="text.primary">
                            {row.label}
                        </Typography>
                        <ChipSkeleton width={70} sx={{marginTop: '4px'}} />
                    </div>
                    <div className="row-data" style={{flexDirection: 'column', alignItems: 'start', width: '100%', padding: '12px 16px'}}>
                        <div className="cell">
                            <Typography variant="caption" color="text.secondary">
                                Scheduled
                            </Typography>
                            <TextSkeleton width={row.isLocation ? '70%' : 64} />
                        </div>
                        <br />
                        <div className="cell">
                            <Typography variant="caption" color="text.secondary">
                                Actual (Caregiver)
                            </Typography>
                            <TextSkeleton width={row.isLocation ? '60%' : 72} />
                        </div>
                    </div>
                </div>
            ))}
    </SkeletonContainer>
)

export default ({rows, skeleton = false}) => {
    const [expanded, setExpanded] = useState(true)
    const {mobile} = useMobile()
    const flaggedCount = rows?.filter(row => row.differs)?.length

    return (
        <div className="card">
            <div className="header" onClick={() => setExpanded(prev => !prev)}>
                <Typography variant="h8" color="text.primary">
                    SCHEDULE VS ACTUAL
                </Typography>
                <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </div>
            <Collapse in={expanded}>
                <br />
                {skeleton && <RowsSkeleton rows={rows} mobile={mobile} />}
                {!skeleton && flaggedCount > 0 && (
                    <>
                        <div className="warning-banner">
                            <WarningAmber color="warning" fontSize="small" />
                            <Typography variant="body2" color="text.primary">
                                {flaggedCount} flagged for review
                            </Typography>
                        </div>
                        <br />
                    </>
                )}
                {!skeleton && !mobile && (
                    <div className="table">
                        <div className="row row-header">
                            <div className="cell-label" />
                            <div className="row-data">
                                <div className="cell">
                                    <Typography variant="caption" color="text.secondary">
                                        SCHEDULED
                                    </Typography>
                                </div>
                                <div className="cell">
                                    <Typography variant="caption" color="text.secondary">
                                        ACTUAL (CAREGIVER)
                                    </Typography>
                                </div>
                                <div className="cell-status" />
                            </div>
                        </div>
                        {rows?.map((row, index) => (
                            <div className="row" key={index}>
                                <div className="cell-label">
                                    <Typography variant="h9" color="text.primary">
                                        {row.label}
                                    </Typography>
                                </div>
                                <div className={`row-data ${row.differs ? 'row-data-differs' : ''}`}>
                                    <div className="cell">
                                        {row.isLocation ? (
                                            <div className="location-map">
                                                <iframe
                                                    src={`https://maps.google.com/maps?q=${encodeURIComponent(row.scheduled)}&output=embed`}
                                                    width="100"
                                                    height="70"
                                                    style={{border: 'none', borderRadius: 8}}
                                                />
                                                <Typography variant="body2" color="text.primary">
                                                    {row.scheduled}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Typography variant="body2" color="text.primary">
                                                {row.scheduled}
                                            </Typography>
                                        )}
                                    </div>
                                    <div className="cell">
                                        {row.isLocation ? (
                                            <div className="location-map">
                                                <iframe
                                                    src={`https://maps.google.com/maps?q=${encodeURIComponent(row.actual)}&output=embed`}
                                                    width="100"
                                                    height="70"
                                                    style={{border: 'none', borderRadius: 8}}
                                                />
                                                <Typography variant="body2" color="text.primary">
                                                    {row.actual}
                                                </Typography>
                                                {row.actual_note && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.actual_note}
                                                    </Typography>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <Typography variant="body2" color="text.primary">
                                                    {row.actual}
                                                </Typography>
                                                {row.actual_note && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.actual_note}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="cell-status">
                                        <Chip label={row.differs ? 'Differs' : 'Matches'} size="small" color={row.differs ? 'warning' : 'success'} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {!skeleton &&
                    mobile &&
                    rows?.map((row, index) => (
                        <div className="row" key={index} style={{flexDirection: 'column', alignItems: 'start'}}>
                            <div className="cell-label">
                                <Typography variant="h9" color="text.primary">
                                    {row.label}
                                </Typography>
                                <Chip
                                    label={row.differs ? 'Differs' : 'Matches'}
                                    size="small"
                                    color={row.differs ? 'warning' : 'success'}
                                    style={{marginTop: 4}}
                                />
                            </div>
                            <div
                                className={`row-data ${row.differs ? 'row-data-differs' : ''}`}
                                style={{flexDirection: 'column', alignItems: 'start', width: '100%', padding: '12px 16px'}}>
                                <div className="cell">
                                    <Typography variant="caption" color="text.secondary">
                                        Scheduled
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {row.scheduled}
                                    </Typography>
                                </div>
                                <br />
                                <div className="cell">
                                    <Typography variant="caption" color="text.secondary">
                                        Actual (Caregiver)
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {row.actual}
                                    </Typography>
                                    {row.actual_note && (
                                        <Typography variant="caption" color="text.secondary">
                                            {row.actual_note}
                                        </Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
            </Collapse>
        </div>
    )
}
